import { ErrorMessage, Field, Form, Formik } from "formik"
import moment from "moment"
import React, { useEffect, useState } from "react"
import * as Yup from "yup"

import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"
import { phpToMoment } from "@mono/essentials/utils/Common"

import MonoSelect from "../Select/MonoSelect"

interface Format {
  id: number
  label: string
  date: string
}

interface MonoMonthYearDayProps {
  onValueChange: (arg0: string) => void
  selectedValue?: any
}

interface FormValues {
  selectedDate: string
  selectedMonth: string
  selectedYear: string
}

const MonoMonthYearDay = ({ onValueChange, selectedValue }: MonoMonthYearDayProps) => {
  const currentYear: number = new Date().getFullYear()
  const [selectedDate, setSelectedDate] = useState<any>("")
  const [selectedMonth, setSelectedMonth] = useState<any>("")
  const [selectedYear, setSelectedYear] = useState<any>("")
  const [selectedTotalMouthDay, setSelectedTotalMouthDay] = useState<any>(null)

  useEffect(() => {
    if (selectedValue?.value) {
      const parsedDate = moment(selectedValue.value)
      setSelectedDate({ label: parsedDate.format("D"), value: parsedDate.format("D") })
      setSelectedMonth({ label: parsedDate.format("MMM"), value: parsedDate.format("MMM") })
      setSelectedYear({ label: parsedDate.format("YYYY"), value: parsedDate.format("YYYY") })
    }
  }, [])

  const handleDateChange = (selectedDate: SelectOptionType) => {
    setSelectedDate(selectedDate)
    updateSelectedDate(selectedDate?.value, selectedMonth?.value, selectedYear?.value)
  }

  const handleMonthChange = (selectedMonth: SelectOptionType) => {
    setSelectedMonth(selectedMonth)
    updateSelectedDate(selectedDate?.value, selectedMonth?.value, selectedYear?.value)
  }

  const handleYearChange = (selectedYear: SelectOptionType) => {
    setSelectedYear(selectedYear)
    updateSelectedDate(selectedDate?.value, selectedMonth?.value, selectedYear?.value)
  }

  const updateSelectedDate = (date: any, month: any, year: any) => {
    if (date && month && year) {
      const formattedDate = moment(`${year}-${month}-${date}`, "YYYY-MMM-D").format(
        phpToMoment("Y-m-d")
      )
      onValueChange?.(formattedDate)
    }
  }

  const renderDateOptions = () => {
    const dateOptions: SelectOptionType[] = []

    for (let i = 1; i <= (selectedTotalMouthDay || 31); i++) {
      dateOptions.push({
        value: i,
        label: `${i}`,
      })
    }

    return dateOptions
  }

  const renderMonthOptions = () => {
    const monthOptions: SelectOptionType[] = []

    for (let i = 1; i <= 12; i++) {
      monthOptions.push({
        label: new Date(2000, i - 1, 1).toLocaleString("default", {
          month: "short",
        }),
        value: new Date(2000, i - 1, 1).toLocaleString("default", {
          month: "short",
        }),
      })
    }

    return monthOptions
  }

  const renderYearOptions = () => {
    const yearOptions: SelectOptionType[] = []

    for (let i = currentYear; i >= 1900; i--) {
      yearOptions.push({
        value: i,
        label: `${i}`,
      })
    }

    return yearOptions
  }

  const getTotalDaysInMonth = (year, month) => {
    let firstDayOfMonth
    if (year && month) {
      firstDayOfMonth = moment({ year, month, day: 1 })
    } else {
      firstDayOfMonth = moment().month(month).startOf("month")
    }
    const lastDayOfMonth = firstDayOfMonth.endOf("month")
    const totalDays = lastDayOfMonth.date()
    return totalDays
  }

  useEffect(() => {
    const mouthList = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sept",
      "Oct",
      "Nov",
      "Dec",
    ]
    const selectedMonthIndex = mouthList?.findIndex((mouth) => mouth === selectedMonth?.value)
    const selectedYearValue = selectedYear?.value
    setSelectedTotalMouthDay(getTotalDaysInMonth(selectedYearValue, selectedMonthIndex))
  }, [selectedMonth, selectedYear])

  const isValidDay = () => {
    if (selectedDate && selectedMonth && selectedYear) {
      const monthIndex = moment().month(selectedMonth.value).month()
      const daysInSelectedMonth = moment({
        year: selectedYear.value,
        month: monthIndex,
      }).daysInMonth()
      return selectedDate.value <= daysInSelectedMonth
    }
    return true
  }

  return (
    <Form className={"w-full"}>
      <div className={"grid grid-cols-3 lg:gap-6 md:gap-3 gap-2"}>
        <div>
          <MonoSelect
            value={selectedMonth}
            name={"Month"}
            placeholder={"Month"}
            menuPosition={"fixed"}
            onValueChange={(value) => {
              handleMonthChange(value)
            }}
            options={renderMonthOptions()}
            selectedValueColor={"text-neutral-800"}
          />
        </div>
        <div>
          <MonoSelect
            value={selectedDate}
            menuPosition={"fixed"}
            placeholder={"Day"}
            name={"Day"}
            onValueChange={(value) => {
              handleDateChange(value)
            }}
            options={renderDateOptions()}
            selectedValueColor={"text-neutral-800"}
          />
        </div>
        <div>
          <MonoSelect
            value={selectedYear}
            name={"Year"}
            placeholder={"Year"}
            menuPosition={"fixed"}
            onValueChange={(value) => {
              handleYearChange(value)
            }}
            options={renderYearOptions()}
            selectedValueColor={"text-neutral-800"}
          />
        </div>
      </div>
      {isValidDay() ? null : (
        <p className={"text12Regular text-red-600 mt-2"}>
          Invalid day for the selected month and year
        </p>
      )}
    </Form>
  )
}

export default MonoMonthYearDay
